import React, { useCallback } from 'react';
import {
  Caption1, Caption1Strong,
} from '@fluentui/react-components';
import { I18nModel } from '@bingads-webui/copilot-i18n';

import { Theme } from './types';
import { InlineCopilotDescription } from '../../Description';
import { RecommendationDialog } from '../RecommendationDialog';
import { useRecommendationDialog } from '../useRecommendationDialog';
import { useThemesRecommendationDialogStyles } from './useThemesRecommendationDialogStyles.styles';
import { ThemesGrid } from './ThemesGrid';
import { HelpPopup } from '../../HelpPopup';

export interface ThemesRecommendationDialogProps {
  className?: string;
  i18n: I18nModel;
  open: boolean;
  finalUrl: string;
  fetchFn: (prompt: string) => Promise<Theme[]>;
  applyFn: (themes: Theme[]) => Promise<void>;
  onClose: () => void;
  onFetchFailed?: (error: any) => void;
  onFetchSucceeded?: (themes: Theme[]) => void;
  onApplyFailed?: (error: any) => void;
  onApplySucceeded?: () => void;
  onSelectedThemesChanged?: (themes: Theme[]) => void;
  isNewLogo?: boolean;
  copyOverrides?: {
    title?: string,
    finalUrl?: string,
    promptDescription?: string,
    description?: string,
    resultsDescription?: string,
    helpTopic?: string,
    helpTopicAlt?: string,
    promptPlaceholder?: string,
    loadingText?: string,
    applyingText?: string,
  };
}

export const ThemesRecommendationDialog: React.FC<ThemesRecommendationDialogProps> = (props) => {
  const {
    className,
    i18n,
    open,
    finalUrl,
    onClose,
    fetchFn,
    applyFn,
    onApplyFailed,
    onApplySucceeded: onApplySucceededProp,
    onFetchFailed,
    onFetchSucceeded,
    onSelectedThemesChanged,
    isNewLogo,
    copyOverrides = {},
  } = props;

  const classes = useThemesRecommendationDialogStyles();

  const onApplySucceeded = useCallback(() => {
    onClose();
    onApplySucceededProp?.();
  }, [onClose, onApplySucceededProp]);

  const {
    prompt,
    results,
    errorMessage,
    isApplying,
    isLoading,
    updatePrompt,
    updateResults,
    fetch,
    apply,
    reset,
  } = useRecommendationDialog({
    fetchFn,
    applyFn,
    onFetchSucceeded,
    onFetchFailed,
    onApplySucceeded,
    onApplyFailed,
  });

  const close = useCallback(() => {
    onClose();
    reset();
  }, [onClose]);

  const selectTheme = useCallback((index: number) => {
    const updatedResults = [...results];
    updatedResults[index].IsSelected = !updatedResults[index].IsSelected;

    updateResults(updatedResults);
    onSelectedThemesChanged?.(updatedResults);
  }, [results, updateResults]);

  const renderTitle = useCallback(() => copyOverrides?.title || i18n.getString(_TL_('Get asset recommendations')), [i18n]);

  const renderDescription = useCallback(() => (
    <div>
      <div className={classes.finalUrlSection}>
        <Caption1Strong>{copyOverrides?.finalUrl || i18n.getString(_TL_('Final URL: '))}</Caption1Strong>
        <Caption1 className="final-url" title={finalUrl}>{finalUrl}</Caption1>
      </div>
      <Caption1Strong className="prompt-description">
        {copyOverrides?.promptDescription || i18n.getString(_TL_('Provide a description of your product.'))}
      </Caption1Strong>
      <InlineCopilotDescription className={classes.description}>
        {copyOverrides?.description || i18n.getString(_TL_('Microsoft Advertising will use the descriptions that you provide to generate new asset recommendations. If your description doesn\'t match your website\'s content, we\'ll generate recommendations entirely from your Final URL.'))}
      </InlineCopilotDescription>
    </div>
  ), [i18n, finalUrl, classes]);

  const renderResults = useCallback(() => (
    <>
      <div className={classes.resultsDescription}>
        <Caption1 className="results-description">{copyOverrides?.resultsDescription || i18n.getString(_TL_('Choose the themes you\'d like to add to your asset group.'))}</Caption1>
        <HelpPopup
          i18n={i18n}
          topic={copyOverrides?.helpTopic || 'pop_BA_Recommendations_Pmax_Recommended_Themes'}
          aria-label={copyOverrides?.helpTopicAlt || i18n.getString(_TL_('Click to learn more about choosing themes'))}
        />
      </div>
      <ThemesGrid disabled={isApplying} themes={results} onSelectTheme={selectTheme} />
    </>
  ), [i18n, classes, isApplying, results, selectTheme]);

  return (
    <RecommendationDialog
      className={className}
      i18n={i18n}
      open={open}
      prompt={prompt}
      results={results}
      isLoading={isLoading}
      isApplying={isApplying}
      errorMessage={errorMessage}
      promptPlaceholder={copyOverrides?.promptPlaceholder || i18n.getString(_TL_('Highlight what you\'d like to include in your ads.'))}
      loadingText={copyOverrides?.loadingText || i18n.getString(_TL_('Generating themes...'))}
      applyingText={copyOverrides?.applyingText || i18n.getString(_TL_('Applying themes...'))}
      onClose={close}
      onPromptChange={updatePrompt}
      onRenderTitle={renderTitle}
      onRenderDescription={renderDescription}
      onRenderResults={renderResults}
      onSubmit={fetch}
      onApply={apply}
      isNewLogo={isNewLogo}
    />
  );
};
